import { MouseEvent, useMemo, useState } from 'react'
import { Score } from 'views/screens/Platform-administrator/Analytics/analytics'

interface IProps {
  userResultData: Score[]
  userResultLabels: string[]
  idealProfileData?: number[]
  idealProfileLabels?: string[]
  labelResult?: string
  userScoreResult?: number[]
}

interface TooltipState {
  x: number
  y: number
  label: string
  teamScore: number
  idealScore?: number
  users?: Score['users']
}

const VIEWBOX_WIDTH = 980
const VIEWBOX_HEIGHT = 760
const CENTER_X = VIEWBOX_WIDTH / 2
const CENTER_Y = 390

const wrapLabel = (label: string, maxCharsPerLine = 20) => {
  if (!label || label.length <= maxCharsPerLine) {
    return [label]
  }

  const words = label.split(' ')
  const lines: string[] = []
  let currentLine = ''

  words.forEach((word) => {
    const nextLine = currentLine ? `${currentLine} ${word}` : word
    if (nextLine.length > maxCharsPerLine && currentLine) {
      lines.push(currentLine)
      currentLine = word
    } else {
      currentLine = nextLine
    }
  })

  if (currentLine) {
    lines.push(currentLine)
  }

  return lines.slice(0, 3)
}

const polarToCartesian = (angle: number, radius: number) => ({
  x: CENTER_X + Math.cos(angle) * radius,
  y: CENTER_Y + Math.sin(angle) * radius,
})

const getPolygonPoints = (values: number[], maxRadius: number) =>
  values
    .map((value, index) => {
      const angle = -Math.PI / 2 + (Math.PI * 2 * index) / values.length
      const radius = (Math.max(0, Math.min(100, value)) / 100) * maxRadius
      const point = polarToCartesian(angle, radius)
      return `${point.x},${point.y}`
    })
    .join(' ')

export default function RadarChart(props: IProps) {
  const { userResultData, idealProfileData, userResultLabels, labelResult, userScoreResult } = props
  const [tooltip, setTooltip] = useState<TooltipState | null>(null)
  const [showIdealProfile, setShowIdealProfile] = useState(true)

  const labelCount = userResultLabels.length
  const maxRadius = labelCount >= 9 ? 170 : labelCount >= 7 ? 190 : 215
  const labelRadius = maxRadius + (labelCount >= 9 ? 92 : 82)
  const labelFontSize = labelCount >= 9 ? 16 : labelCount >= 7 ? 17 : 18
  const tickFontSize = labelCount >= 9 ? 12 : 13
  const pointRadius = labelCount >= 9 ? 5 : 6
  const teamValues = userScoreResult || userResultData.map((scoreObj) => scoreObj.mean)
  const gridLevels = [20, 40, 60, 80, 100]

  const axes = useMemo(
    () =>
      userResultLabels.map((label, index) => {
        const angle = -Math.PI / 2 + (Math.PI * 2 * index) / labelCount
        const lineEnd = polarToCartesian(angle, maxRadius)
        const labelPoint = polarToCartesian(angle, labelRadius)
        const anchor =
          Math.cos(angle) > 0.28 ? 'start' : Math.cos(angle) < -0.28 ? 'end' : 'middle'
        const wrappedLabel = wrapLabel(label, labelCount >= 9 ? 18 : 20)

        return {
          angle,
          lineEnd,
          labelPoint,
          anchor,
          wrappedLabel,
        }
      }),
    [userResultLabels, labelCount, maxRadius, labelRadius]
  )

  const teamPolygon = useMemo(() => getPolygonPoints(teamValues, maxRadius), [teamValues, maxRadius])
  const idealPolygon = useMemo(
    () => (idealProfileData?.length && showIdealProfile ? getPolygonPoints(idealProfileData, maxRadius) : ''),
    [idealProfileData, maxRadius, showIdealProfile]
  )

  const handlePointHover = (
    event: MouseEvent<SVGCircleElement>,
    label: string,
    teamScore: number,
    users: Score['users'],
    idealScore?: number
  ) => {
    const bounds = event.currentTarget.ownerSVGElement?.getBoundingClientRect()
    if (!bounds) return

    setTooltip({
      x: event.clientX - bounds.left + 18,
      y: event.clientY - bounds.top - 18,
      label,
      teamScore,
      idealScore,
      users,
    })
  }

  if (!labelCount) {
    return null
  }

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '980px',
        margin: '0 auto',
        padding: '8px 0 16px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '28px',
          flexWrap: 'wrap',
          marginBottom: '12px',
        }}
      >
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
          <span
            style={{
              width: '34px',
              height: '12px',
              borderRadius: '999px',
              background: 'rgba(54, 162, 235, 0.2)',
              border: '2px solid rgba(54, 162, 235, 1)',
            }}
          />
          <span style={{ fontSize: '16px', fontWeight: 700, color: '#365066' }}>{labelResult || 'Promedio'}</span>
        </div>

        {idealProfileData?.length ? (
          <button
            type="button"
            onClick={() => setShowIdealProfile((currentState) => !currentState)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              padding: '8px 12px',
              borderRadius: '999px',
              border: showIdealProfile ? '1px solid rgba(235, 54, 223, 0.26)' : '1px solid rgba(108, 125, 143, 0.22)',
              background: showIdealProfile ? 'rgba(246, 90, 249, 0.08)' : 'rgba(255,255,255,0.75)',
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                width: '34px',
                height: '12px',
                borderRadius: '999px',
                background: showIdealProfile ? 'rgba(246, 90, 249, 0.18)' : 'rgba(199, 208, 218, 0.3)',
                border: showIdealProfile ? '2px solid #eb36df' : '2px solid #b3bdc8',
              }}
            />
            <span style={{ fontSize: '16px', fontWeight: 700, color: '#365066' }}>
              {showIdealProfile ? 'Ocultar perfil ideal' : 'Mostrar perfil ideal'}
            </span>
          </button>
        ) : null}
      </div>

      <div
        style={{
          position: 'relative',
          width: '100%',
          minHeight: '620px',
        }}
      >
        <svg
          viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
          width="100%"
          height="100%"
          style={{ display: 'block', overflow: 'visible' }}
          onMouseLeave={() => setTooltip(null)}
        >
          {gridLevels.map((level) => {
            const levelRadius = (level / 100) * maxRadius
            const points = axes
              .map(({ angle }) => {
                const point = polarToCartesian(angle, levelRadius)
                return `${point.x},${point.y}`
              })
              .join(' ')

            return (
              <g key={`grid-${level}`}>
                <polygon
                  points={points}
                  fill={level === 100 ? 'rgba(244, 248, 252, 0.55)' : 'transparent'}
                  stroke="rgba(143, 164, 182, 0.35)"
                  strokeWidth={1.4}
                />
                <text
                  x={CENTER_X + 10}
                  y={CENTER_Y - levelRadius + 4}
                  fill="#7d8ea2"
                  fontSize={tickFontSize}
                  fontWeight={600}
                >
                  {level}
                </text>
              </g>
            )
          })}

          {axes.map(({ lineEnd, angle, labelPoint }, index) => {
            const connectorStart = polarToCartesian(angle, maxRadius + 12)
            const connectorEnd = polarToCartesian(angle, labelRadius - 22)

            return (
              <g key={`axis-${index}`}>
                <line
                  x1={CENTER_X}
                  y1={CENTER_Y}
                  x2={lineEnd.x}
                  y2={lineEnd.y}
                  stroke="rgba(143, 164, 182, 0.28)"
                  strokeWidth={1.2}
                />
                <line
                  x1={connectorStart.x}
                  y1={connectorStart.y}
                  x2={connectorEnd.x}
                  y2={connectorEnd.y}
                  stroke="rgba(143, 164, 182, 0.34)"
                  strokeWidth={1}
                  strokeDasharray="4 5"
                />
                <circle cx={labelPoint.x} cy={labelPoint.y} r={1.6} fill="rgba(143, 164, 182, 0.36)" />
              </g>
            )
          })}

          {idealProfileData?.length && showIdealProfile ? (
            <polygon
              points={idealPolygon}
              fill="rgba(246, 90, 249, 0.18)"
              stroke="#eb36df"
              strokeWidth={3}
              strokeLinejoin="round"
            />
          ) : null}

          <polygon
            points={teamPolygon}
            fill="rgba(54, 162, 235, 0.18)"
            stroke="rgba(54, 162, 235, 1)"
            strokeWidth={3}
            strokeLinejoin="round"
          />

          {axes.map(({ angle }, index) => {
            const teamScore = teamValues[index] || 0
            const teamPoint = polarToCartesian(angle, (teamScore / 100) * maxRadius)
            const idealScore = showIdealProfile ? idealProfileData?.[index] : undefined
            const idealPoint =
              typeof idealScore === 'number'
                ? polarToCartesian(angle, (idealScore / 100) * maxRadius)
                : null

            return (
              <g key={`points-${index}`}>
                {idealPoint ? <circle cx={idealPoint.x} cy={idealPoint.y} r={pointRadius - 1} fill="#eb36df" /> : null}

                <circle
                  cx={teamPoint.x}
                  cy={teamPoint.y}
                  r={pointRadius}
                  fill="#ffffff"
                  stroke="rgba(54, 162, 235, 1)"
                  strokeWidth={3}
                  onMouseEnter={(event) =>
                    handlePointHover(
                      event,
                      userResultLabels[index],
                      teamScore,
                      userResultData[index]?.users || [],
                      idealScore
                    )
                  }
                  onMouseMove={(event) =>
                    handlePointHover(
                      event,
                      userResultLabels[index],
                      teamScore,
                      userResultData[index]?.users || [],
                      idealScore
                    )
                  }
                />
              </g>
            )
          })}

          {axes.map(({ labelPoint, anchor, wrappedLabel }, index) => (
            <text
              key={`label-${index}`}
              x={labelPoint.x}
              y={labelPoint.y}
              textAnchor={anchor}
              fill="#4f6172"
              fontSize={labelFontSize}
              fontWeight={600}
            >
              {wrappedLabel.map((line, lineIndex) => (
                <tspan
                  key={`${line}-${lineIndex}`}
                  x={labelPoint.x}
                  dy={lineIndex === 0 ? 0 : labelFontSize + 4}
                >
                  {line}
                </tspan>
              ))}
            </text>
          ))}
        </svg>

        {tooltip ? (
          <div
            style={{
              position: 'absolute',
              left: tooltip.x,
              top: tooltip.y,
              minWidth: '250px',
              maxWidth: '320px',
              padding: '14px 16px',
              borderRadius: '16px',
              background: 'rgba(20, 36, 52, 0.94)',
              color: '#ffffff',
              boxShadow: '0 20px 40px rgba(17, 33, 49, 0.22)',
              pointerEvents: 'none',
              zIndex: 3,
            }}
          >
            {(() => {
              const gap =
                typeof tooltip.idealScore === 'number'
                  ? Number((tooltip.idealScore - tooltip.teamScore).toFixed(1))
                  : null
              const sortedUsers = [...(tooltip.users || [])].sort(
                (firstUser, secondUser) => secondUser.competence_score - firstUser.competence_score
              )

              return (
                <>
            <div style={{ fontSize: '14px', fontWeight: 800, marginBottom: '8px' }}>{tooltip.label}</div>
            <div style={{ fontSize: '13px', lineHeight: 1.5 }}>
              <div>Promedio del equipo: {Number(tooltip.teamScore.toFixed(1))}</div>
              {typeof tooltip.idealScore === 'number' ? <div>Perfil ideal: {tooltip.idealScore}</div> : null}
              {gap !== null ? (
                <div style={{ marginTop: '8px' }}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '3px 10px',
                      borderRadius: '999px',
                      background: gap > 0 ? 'rgba(255, 107, 107, 0.18)' : 'rgba(61, 196, 126, 0.18)',
                      color: gap > 0 ? '#ff9a9a' : '#8ef0b3',
                      fontWeight: 800,
                    }}
                  >
                    Brecha: {gap > 0 ? `+${gap}` : gap}
                  </span>
                </div>
              ) : null}
            </div>

            {sortedUsers.length ? (
              <div
                style={{
                  marginTop: '10px',
                  paddingTop: '10px',
                  borderTop: '1px solid rgba(255,255,255,0.14)',
                  fontSize: '12px',
                  lineHeight: 1.45,
                }}
              >
                {sortedUsers.map((user) => (
                  <div key={`${tooltip.label}-${user.user_id}`}>
                    {user.full_name}: {user.competence_score}
                  </div>
                ))}
              </div>
            ) : null}
                </>
              )
            })()}
          </div>
        ) : null}
      </div>
    </div>
  )
}
